import { useEffect, useMemo, useState } from "react";
import { Search, User, Users, Tag, SlidersHorizontal, ChevronDown, ChevronUp, Braces } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BASE_SECTIONS = [
  { id: "anagrafica", label: "Anagrafica", icon: Users, fields: [
    { key: "nome", label: "Nome" }, { key: "cognome", label: "Cognome" }, { key: "phone", label: "Telefono" }, { key: "email", label: "Email" }] },
  { id: "promo", label: "Promozione", icon: Tag, fields: [
    { key: "sconto", label: "Sconto" }, { key: "scadenza", label: "Scadenza" }, { key: "codice", label: "Codice" }, { key: "risposta", label: "Parola di risposta" }] },
];

export function insertAtCursor(el, value, token) {
  const start = el?.selectionStart ?? value.length;
  const end = el?.selectionEnd ?? value.length;
  const before = value.slice(0, start), after = value.slice(end);
  const sp1 = before && !/\s$/.test(before) ? " " : "", sp2 = after && !/^\s/.test(after) ? " " : "";
  const next = `${before}${sp1}${token}${sp2}${after}`;
  const pos = (before + sp1 + token).length;
  return { value: next, pos };
}

export function DynamicFieldsDialog({ open, onOpenChange, customFields = [], onPick }) {
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => { if (open) setQ(""); }, [open]);
  const sections = useMemo(() => [...BASE_SECTIONS, { id: "custom", label: "Personalizzato", icon: SlidersHorizontal,
    fields: customFields.map((k) => ({ key: k, label: k.replace(/_/g, " ") })) }], [customFields]);
  const needle = q.trim().toLowerCase();
  const visible = sections.map((s) => ({ ...s, fields: s.fields.filter((f) => !needle || f.key.toLowerCase().includes(needle) || f.label.toLowerCase().includes(needle)) }))
    .filter((s) => s.fields.length || (!needle && s.id === "custom"));
  const allCollapsed = sections.every((s) => collapsed[s.id]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto p-0" data-testid="dynamic-fields-dialog">
        <DialogHeader className="px-5 pt-5"><DialogTitle className="font-display">Aggiungi campo dinamico</DialogTitle></DialogHeader>
        <div className="px-5 pb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="bg-[#0A0E17] border-[#1F2937] pr-8" data-testid="dynamic-fields-search" />
            <Search className="h-4 w-4 text-slate-500 absolute right-2.5 top-2.5" />
          </div>
          <button type="button" onClick={() => setCollapsed(allCollapsed ? {} : Object.fromEntries(sections.map((s) => [s.id, true])))}
                  className="text-xs text-slate-400 hover:text-slate-200 whitespace-nowrap" data-testid="dynamic-fields-toggle-all">{allCollapsed ? "Espandi sezioni" : "Riduci sezioni"}</button>
        </div>
        <div className="divide-y divide-[#1F2937]">
          {visible.map(({ id, label, icon: Icon, fields }) => (
            <div key={id} data-testid={`dynamic-section-${id}`}>
              <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [id]: !c[id] }))}
                      className="w-full flex items-center gap-3 px-5 py-2.5 bg-[#0F172A] text-sm font-semibold text-white">
                <Icon className="h-4 w-4 text-[#10B981]" /> <span className="text-white">{label}</span>
                <span className="ml-auto text-slate-500">{collapsed[id] ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}</span>
              </button>
              {!collapsed[id] && (fields.length ? fields.map((f) => (
                <button key={f.key} type="button" onClick={() => { onPick(`{{${f.key}}}`); onOpenChange(false); }}
                        className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[#1E293B] transition-colors" data-testid={`dynamic-field-${f.key}`}>
                  <User className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-100 capitalize">{f.label}</span>
                    <span className="inline-block mt-0.5 rounded bg-[#0A0E17] border border-[#1F2937] px-1.5 text-[10px] font-mono2 text-slate-400">{`{{${f.key}}}`}</span>
                  </span>
                </button>
              )) : <p className="px-5 py-3 text-xs text-slate-500">Nessun campo personalizzato: si creano importando contatti con colonne aggiuntive o dalla scheda contatto.</p>)}
            </div>
          ))}
        </div>
        <p className="px-5 py-3 text-[11px] text-slate-500 flex items-start gap-2"><Braces className="h-3.5 w-3.5 mt-0.5 shrink-0" />Il campo viene inserito nel punto del testo in cui si trova il cursore. Usa <span className="font-mono2">{"{{campo|valore di riserva}}"}</span> per un testo predefinito se il dato manca.</p>
      </DialogContent>
    </Dialog>
  );
}
